import { useEffect, useState } from "react";

interface LoadingAnnouncerProps {
  isLoading: boolean;
  loadingMessage?: string;
  loadedMessage?: string;
}

/**
 * Accessible loading state announcer for screen readers.
 * Uses aria-live to announce loading state changes.
 */
export function LoadingAnnouncer({
  isLoading,
  loadingMessage = "Loading content, please wait...",
  loadedMessage = "Content loaded.",
}: LoadingAnnouncerProps) {
  const [announcement, setAnnouncement] = useState<string>("");

  useEffect(() => {
    if (isLoading) {
      setAnnouncement(loadingMessage);
    } else {
      setAnnouncement(loadedMessage);
      // Clear after announcement
      const timer = setTimeout(() => setAnnouncement(""), 1000);
      return () => clearTimeout(timer);
    }
  }, [isLoading, loadingMessage, loadedMessage]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    >
      {announcement}
    </div>
  );
}

/**
 * Hook for managing loading announcements
 */
export function useLoadingAnnouncement(isLoading: boolean, context?: string) {
  const loadingMessage = context 
    ? `Loading ${context}, please wait...` 
    : "Loading content, please wait...";
  const loadedMessage = context 
    ? `${context} loaded.` 
    : "Content loaded.";

  return { loadingMessage, loadedMessage };
}
